/**
 * `?gpucheck` (dev; the coordinator's ask for the owner's one-sitting GPU check): the presentation features waiting on the
 * owner's GPU figures, each measured off and then on in the owner's own browser, on a fixed scene with the camera on a fixed
 * path, and a table at the end to copy back.
 *
 * Open `/?gpucheck` and leave the tab in front. The page loads each step in turn (a scene, one feature off or on — every other
 * feature at its default), warms up `gpuwarm` seconds with the camera at the path's start, then flies the path for `gpusec`
 * seconds and measures; the steps carry their results in sessionStorage, and the last page shows the table. Optional:
 * `&gpuscenes=mega,story`, `&gpufeatures=groundlook,mstyle,…,all`, `&gpusec=8`, `&gpuwarm=3`, `&gpusync=0` (no frame in
 * series: the frames overlap as in play), `&gpuextra=<parameters, URL-encoded>` over every step (a dial's variant: `strata%3Doff`).
 *
 * - **The scenes**: `mega` — lab-38 on thalia, the megaplanet, 25 a side, the landforms on; `story` — the Lab's Story preset.
 * - **The features**: 71.8's ground look (picks), 388's mountain style, 380's craterlet marks (on: 24 marks round the fine
 *   patch's focus, the shader's most), 74.16's aurora, the weather as drawn (the fall, the sky's weather and the cells' volumes),
 *   74.15's finer mesh (a world change: the fight differs between its two steps), the tree sway; and all of them off, then on.
 *   The URL rows where a feature has one; the aurora, the weather's drawing and the marks are switched here on the running game.
 * - **The measure**: per frame, the frame in series — from its callbacks' start until the GPU has done its work (a pixel of
 *   the canvas read back at the frame's end: Chrome's gl.finish() returns without waiting), so the on step's p50 less the off
 *   step's is the feature's cost, CPU and GPU; the CPU's time in the frame's callbacks (that cost's CPU share); the timer
 *   query's sum over every `renderer.render` (EXT_disjoint_timer_query_webgl2; a disjoint frame dropped) — on ANGLE's Metal
 *   backend (Chrome on a Mac) that sums each command buffer's GPU start-to-end, and the buffers overlap, so it overstates
 *   10–20× (Oct 31, lane E: a 16× noise shader read +106 ms on it, +6.6 ms in series): its direction only; the rAF interval;
 *   the draws and triangles a frame (384: read once a frame — summed per render call they counted the view once for each
 *   of the composer's ~18 calls); and each pass's share of the timer's sum (the scene, the bloom, the output, the
 *   antialiasing): shares, as the timer's own ms overstate.

 * - **384, the cuts** (asked for by name: `&gpufeatures=fxaa,noaa,dpr,nofx`; each off is today's look): FXAA in SMAA's place,
 *   no antialiasing, the pixel ratio at most 1.25, the post chain off (rendering/renderer.ts's `aa`, `dpr` and `fx`).
 *
 * Nothing in the sim: the match plays as launched (team 0 left idle; its seat's look held a little up, `GPU_AIM_LIFT`, so the
 * host's crosshair does the same work in every step), the camera and the drawing are this page's. Not in the
 * browser smoke (its cases never carry `gpucheck`).
 */
import * as THREE from 'three';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import type { GameState, PlanetState, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT, personSetOf } from '../core/scaleProfile';
import { getSurfaceRadius } from '../core/surface';
import { battlePlanetIndex } from '../core/actionSetup';
import { ViewmodelPass } from '../rendering/viewmodel';

const KEY = 'orbituary-gpucheck';

/** The scenes: the launch query each step starts from. */
export const GPU_SCENES: Record<string, { label: string; query: string }> = {
  mega: { label: 'megaplanet lab-38 thalia, 25 a side, landforms', query: 'action&skirmish&scale=human&seed=lab-38&planet=thalia&planetSize=megaplanet&units=25&landforms=1' },
  story: { label: 'the Story preset', query: 'preset=Story&action&skirmish' },
};

/** A feature's two states: the URL's parameters, and what is switched on the running game. */
interface FeatureState { url: Record<string, string>; aurora?: boolean; weather?: boolean; marks?: boolean }
export const GPU_FEATURES: Record<string, { label: string; off: FeatureState; on: FeatureState }> = {
  groundlook: { label: '71.8 ground look (picks)', off: { url: { groundlook: 'today' } }, on: { url: { groundlook: 'picks' } } },
  mstyle: { label: '388 mountain style', off: { url: { mstyle: '0' } }, on: { url: { mstyle: '1' } } },
  marks: { label: '380 craterlet marks (24)', off: { url: {}, marks: false }, on: { url: {}, marks: true } },
  aurora: { label: '74.16 aurora', off: { url: {}, aurora: false }, on: { url: {}, aurora: true } },
  weather: { label: 'weather as drawn', off: { url: {}, weather: false }, on: { url: {}, weather: true } },
  finermesh: { label: '74.15 finer mesh (world)', off: { url: { finerMesh: '0' } }, on: { url: { finerMesh: '1' } } },
  treesway: { label: 'tree sway', off: { url: { treesway: '0' } }, on: { url: { treesway: '1' } } },
  clips: { label: '401 soldier clips (CC0)', off: { url: { clips: '0' } }, on: { url: { clips: '1' } } },   // (DECISIONS 401: the CPU's per-figure blend; not in `all`)
  // 384, the cuts (each off is today's look; asked for by name, `&gpufeatures=fxaa,noaa,dpr,nofx`, not in the default run):
  // FXAA, one full-size pass, in SMAA's three's place; no antialiasing; the pixel ratio at most 1.25; the post chain off
  fxaa: { label: '384 cut: FXAA for SMAA', off: { url: {} }, on: { url: { aa: 'fxaa' } } },
  noaa: { label: '384 cut: no antialiasing', off: { url: {} }, on: { url: { aa: 'none' } } },
  dpr: { label: '384 cut: pixel ratio ≤ 1.25', off: { url: {} }, on: { url: { dpr: '1.25' } } },
  nofx: { label: '384 cut: no post chain (fx=0)', off: { url: {} }, on: { url: { fx: '0' } } },
  all: {
    label: 'all of them',
    off: { url: { groundlook: 'today', mstyle: '0', finerMesh: '0', treesway: '0' }, aurora: false, weather: false, marks: false },
    on: { url: { groundlook: 'picks', mstyle: '1', finerMesh: '1', treesway: '1' }, aurora: true, weather: true, marks: true },
  },
};

interface Step { scene: string; feature: string; state: 'off' | 'on' }
interface Stats { p50: number; p95: number; p99: number; n: number }
/** 384: the cuts, run when asked for by name (`&gpufeatures=`), not in the default run. */
export const GPU_CUTS = ['fxaa', 'noaa', 'dpr', 'nofx'];

interface StepResult extends Step {
  gpu: Stats | null; raf: Stats; cpu: Stats; draws: number; tris: number;
  /** the frame in series: its callbacks, then the GPU waited out (a pixel of the canvas read back); null with `gpusync=0` */
  frame?: Stats | null;
  /** 384: each pass's share of the timer's sum over the measured frames (the scene, the bloom, the output, the antialiasing; `other`: a render outside the composer) — a share, as the timer's ms overstate on ANGLE-Metal */
  passes?: Record<string, number>;
  /** the share of measured frames an aurora showed (drawn, its gain over nothing: the night side, the wind); the weather's kind at the end */
  auroraShown: number; weather: string | null; error?: string;
}
interface Run { steps: Step[]; at: number; results: StepResult[]; sec: number; warm: number; device: string; extra?: string; sync?: boolean }

const params = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;

function load(): Run | null { try { const s = sessionStorage.getItem(KEY); return s ? JSON.parse(s) as Run : null; } catch { return null; } }
function save(r: Run): void { try { sessionStorage.setItem(KEY, JSON.stringify(r)); } catch { /* (a private window: the run can't carry) */ } }

/** The plan: each scene, each feature off then on. */
export function gpuCheckPlan(scenes: string[], features: string[]): Step[] {
  const out: Step[] = [];
  for (const scene of scenes) for (const feature of features) for (const state of ['off', 'on'] as const) out.push({ scene, feature, state });
  return out;
}

/** A step's page: the scene's query, the feature state's parameters over it, and the step's index. */
export function gpuStepQuery(step: Step, index: number, extra = ''): string {
  const q = new URLSearchParams(GPU_SCENES[step.scene].query);
  for (const [k, v] of new URLSearchParams(extra)) q.set(k, v);
  for (const [k, v] of Object.entries(GPU_FEATURES[step.feature][step.state].url)) q.set(k, v);
  q.set('gpucheck', 'run'); q.set('gpustep', String(index));
  return '?' + q.toString().replace(/=(?=&|$)/g, '');
}

export function percentiles(xs: number[]): Stats {
  const s = xs.slice().sort((a, b) => a - b), at = (p: number) => s.length ? s[Math.min(s.length - 1, Math.floor(p * s.length))] : NaN;
  return { p50: at(0.5), p95: at(0.95), p99: at(0.99), n: s.length };
}

function go(q: string): void { location.href = location.pathname + q; }

// ---- the progress line and the table ------------------------------------------------------------------------------------------

function banner(text: string): HTMLDivElement {
  let d = document.getElementById('gpucheck-banner') as HTMLDivElement | null;
  if (!d) {
    d = document.createElement('div');
    d.id = 'gpucheck-banner';
    d.style.cssText = 'position:fixed;top:8px;left:50%;transform:translateX(-50%);z-index:99999;background:#111c;color:#fff;font:13px system-ui;padding:6px 12px;border-radius:6px;pointer-events:none';
    document.body.appendChild(d);
  }
  d.textContent = text;
  return d;
}

const f1 = (x: number | undefined) => x === undefined || !Number.isFinite(x) ? '–' : x.toFixed(2);

/** The results as a markdown table (what the Copy button copies). */
export function gpuCheckTable(run: Run): string {
  const lines = [`GPU check (${new Date(run.at).toISOString().slice(0, 16)}; ${run.device}; ${run.sec} s a step after ${run.warm} s${run.extra ? `; over every step: ${run.extra}` : ''}${run.sync === false ? '; gpusync=0' : ''})`, ''];
  lines.push('ms a frame. frame: from its callbacks\' start until the GPU has done it (CPU and GPU in series; on − off is the feature\'s cost, its CPU share in the CPU column).'
    + ' timer: the GPU timer query — on Chrome on a Mac (ANGLE-Metal) it overstates 10–20×, read its direction only. rAF: the frame interval.');
  lines.push('');
  lines.push('| scene | feature | frame p50 off→on | frame p95 off→on | CPU p50 off→on | timer p50 off→on | rAF p50 off→on | draws off→on | tris off→on | note |');
  lines.push('|---|---|---|---|---|---|---|---|---|---|');
  const by = new Map<string, { off?: StepResult; on?: StepResult }>();
  for (const r of run.results) { const k = `${r.scene}|${r.feature}`; const e = by.get(k) ?? {}; e[r.state] = r; by.set(k, e); }
  for (const [k, { off, on }] of by) {
    const [scene, feature] = k.split('|');
    const pair = (g: (r: StepResult) => number | undefined) => `${f1(off && g(off))} → ${f1(on && g(on))}`;
    const note = [off?.error && `off: ${off.error}`, on?.error && `on: ${on.error}`,
      feature === 'aurora' || feature === 'all' ? `aurora shown ${Math.round(100 * (on?.auroraShown ?? 0))} % of frames` : '',
      feature === 'weather' || feature === 'all' ? `weather ${on?.weather ?? '–'}` : ''].filter(Boolean).join('; ');
    lines.push(`| ${scene} | ${GPU_FEATURES[feature]?.label ?? feature} | ${pair(r => r.frame?.p50)} | ${pair(r => r.frame?.p95)} | ${pair(r => r.cpu.p50)} | ${pair(r => r.gpu?.p50)} | ${pair(r => r.raf.p50)} | ${off && on ? `${off.draws} → ${on.draws}` : '–'} | ${off && on ? `${Math.round(off.tris / 1000)}k → ${Math.round(on.tris / 1000)}k` : '–'} | ${note} |`);
  }
  // 384: under the frame's cost, where the GPU's share of it goes — each pass's share of the timer's sum (shares: its ms overstate)
  const withPasses = run.results.filter(r => r.passes);
  if (withPasses.length) {
    const order = ['scene', 'viewmodel', 'bloom', 'output', 'smaa', 'fxaa', 'other'], cols = order.filter(n => withPasses.some(r => r.passes![n] !== undefined));
    const pc = (x: number | undefined) => x === undefined ? '–' : `${Math.round(100 * x)} %`;
    lines.push('', 'The GPU by pass: each pass\'s share of the timer (the frame column above is the cost; the timer\'s own ms overstate)', '',
      `| scene | feature | state | ${cols.join(' | ')} |`, `|---|---|---|${cols.map(() => '---|').join('')}`);
    for (const r of withPasses) lines.push(`| ${r.scene} | ${GPU_FEATURES[r.feature]?.label ?? r.feature} | ${r.state} | ${cols.map(n => pc(r.passes![n])).join(' | ')} |`);
  }
  return lines.join('\n');
}

function showTable(run: Run): void {
  const text = gpuCheckTable(run);
  const box = document.createElement('div');
  box.style.cssText = 'position:fixed;inset:24px;z-index:99999;background:#14161b;color:#e8e8e8;font:13px system-ui;padding:16px;border-radius:8px;overflow:auto;box-shadow:0 4px 24px #000a';
  const h = document.createElement('div');
  h.style.cssText = 'display:flex;gap:8px;align-items:center;margin-bottom:10px';
  h.innerHTML = '<b style="font-size:15px">GPU check — done</b><span style="flex:1"></span>';
  const copy = document.createElement('button'), again = document.createElement('button');
  copy.textContent = 'Copy the table'; again.textContent = 'Run again';
  for (const b of [copy, again]) b.style.cssText = 'font:13px system-ui;padding:6px 12px;border-radius:5px;border:0;background:#3d6df2;color:#fff;cursor:pointer';
  copy.onclick = () => { void navigator.clipboard?.writeText(text).then(() => { copy.textContent = 'Copied'; }, () => { pre.focus(); }); };
  again.onclick = () => { try { sessionStorage.removeItem(KEY); } catch { /* */ } go('?gpucheck'); };
  h.append(copy, again);
  const pre = document.createElement('textarea');
  pre.value = text; pre.readOnly = true;
  pre.style.cssText = 'width:100%;height:calc(100% - 50px);background:#0c0d10;color:#e8e8e8;font:12px ui-monospace,monospace;border:0;padding:8px;white-space:pre';
  box.append(h, pre);
  document.body.appendChild(box);
}

// ---- a step: wait for the match, switch, warm up, fly the path, measure --------------------------------------------------------

/** The game's parts a step reads and switches (private fields of the game: a dev page's reach). */
interface GameParts {
  match?: { state: GameState };
  actionHost?: { controlled: number | null; pitch?: number };
  sceneObjs?: { renderer: THREE.WebGLRenderer; camera: THREE.PerspectiveCamera; composer?: { passes: object[] } | null };
  renderFrame?: () => void;
  updateFineGround?: () => void;
  auroras?: { ovals: { mesh: THREE.Object3D; material: THREE.ShaderMaterial }[]; update: (...a: unknown[]) => void } | null;
  weatherView?: { update: (...a: unknown[]) => unknown; kindNow?: string | null } & Record<string, unknown>;
  weatherCellsView?: { update: (state: GameState, planetIndex: number, ...rest: unknown[]) => void };
  fineGround?: (Record<string, unknown> & { syncMarks?: (planet: PlanetState, ...rest: unknown[]) => void }) | null;
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

/**
 * 384: the idle seat's look pitch, held over every step (radians up). The host marches the crosshair's ray from the seat
 * each frame (actionHost placeCrosshair: a quarter person a step, every person, the ground and the props) — level, that ray
 * grazed the ground, met it a step out on one mesh and ran to the rifle's whole reach on the other (74.15's finer mesh read
 * 13.8 → 5.6 ms of CPU, Oct 31: the same fight, a different crosshair). A little up, it clears the ground in every step:
 * the same work everywhere, a player's look along the horizon.
 */
export const GPU_AIM_LIFT = 0.2;

/** 384: the frame's passes as the table names them (by class: a minified build renames them). */
export function passLabel(pass: object | null): string {
  return pass instanceof RenderPass ? 'scene' : pass instanceof UnrealBloomPass ? 'bloom' : pass instanceof OutputPass ? 'output'
    : pass instanceof SMAAPass ? 'smaa' : pass instanceof ShaderPass ? 'fxaa' : pass instanceof ViewmodelPass ? 'viewmodel' : 'other';
}

/** The fixed path: from a ground eye at the battle's centre out 80 people round it, rising to 150 people and back, looking at the centre. */
function pathEye(planet: PlanetState, centre: THREE.Vector3, P: number, s: number, eye: THREE.Vector3, at: THREE.Vector3, up: THREE.Vector3): void {
  const c = centre, e1 = new THREE.Vector3(0, 1, 0).cross(c);
  if (e1.lengthSq() < 1e-6) e1.set(1, 0, 0).cross(c);
  e1.normalize();
  const e2 = c.clone().cross(e1).normalize(), az = 2 * Math.PI * s, ang = 80 * P / planet.radius;
  const lift = 2.6 + 150 * 0.5 * (1 - Math.cos(2 * Math.PI * s));
  const d = c.clone().multiplyScalar(Math.cos(ang)).addScaledVector(e1.multiplyScalar(Math.cos(az)).addScaledVector(e2, Math.sin(az)), Math.sin(ang)).normalize();
  const C = planet.position, ground = getSurfaceRadius(planet, d as Vec3), g0 = getSurfaceRadius(planet, c as Vec3);
  eye.set(C.x, C.y, C.z).addScaledVector(d, ground + lift * P);
  at.set(C.x, C.y, C.z).addScaledVector(c, g0 + 10 * P);
  up.copy(d);
}

/** 24 craterlets round the patch's focus (the marks' most), each a grenade's in the ground's material — the shader's worst case. */
function marksAround(planet: PlanetState, focus: THREE.Vector3, P: number): PlanetState['craterlets'] {
  const C = planet.position, c = new THREE.Vector3(focus.x - C.x, focus.y - C.y, focus.z - C.z).normalize();
  const e1 = new THREE.Vector3(0, 1, 0).cross(c).normalize(), e2 = c.clone().cross(e1);
  return Array.from({ length: 24 }, (_, i) => {
    const r = (4 + 3 * (i % 6)) * P / planet.radius, a = i * 2.39996;
    const d = c.clone().multiplyScalar(Math.cos(r)).addScaledVector(e1.clone().multiplyScalar(Math.cos(a)).addScaledVector(e2, Math.sin(a)), Math.sin(r)).normalize();
    return { id: -1 - i, kind: 'grenade' as const, material: 'soil' as const, dir: { x: d.x, y: d.y, z: d.z }, scale: 1 };
  });
}

async function runStep(run: Run, index: number): Promise<void> {
  const step = run.steps[index], fs = GPU_FEATURES[step.feature][step.state];
  const label = `GPU check ${index + 1}/${run.steps.length}: ${step.scene} · ${GPU_FEATURES[step.feature].label} ${step.state}`;
  banner(`${label} — loading`);
  const result: StepResult = { ...step, gpu: null, raf: percentiles([]), cpu: percentiles([]), draws: 0, tris: 0, auroraShown: 0, weather: null };
  const finish = (error?: string) => {
    if (error) result.error = error;
    run.results.push(result);
    save(run);
    if (index + 1 < run.steps.length) go(gpuStepQuery(run.steps[index + 1], index + 1, run.extra)); else go('?gpucheck=done');
  };
  // the match: the game exposed, its state and renderer up, a unit of team 0 placed
  let g: GameParts | undefined;
  for (let t = 0; t < 240; t++) {
    g = (globalThis as unknown as { __orbituaryGame?: GameParts }).__orbituaryGame;
    if (g?.match?.state && g.sceneObjs?.renderer && g.actionHost) break;
    await sleep(500);
  }
  if (!g?.match?.state || !g.sceneObjs?.renderer) return finish('the match did not start in 2 minutes');
  const game = g, renderer = g.sceneObjs!.renderer, camera = g.sceneObjs!.camera, gl = renderer.getContext() as WebGL2RenderingContext;
  const state0 = game.match!.state, pi = battlePlanetIndex(state0), P = PERSON_BODY_HEIGHT * personSetOf(state0).k;
  const me = state0.players.filter(p => p.teamIndex === 0 && p.planetIndex === pi).sort((a, b) => a.id - b.id)[0];
  const centre = new THREE.Vector3(me?.surfaceDir.x ?? 0, me?.surfaceDir.y ?? 1, me?.surfaceDir.z ?? 0).normalize();

  // the switches on the running game
  if (fs.aurora === false && game.auroras) { game.auroras.update = () => {}; for (const o of game.auroras.ovals) o.mesh.visible = false; }
  if (fs.weather === false) {
    const wv = game.weatherView, cv = game.weatherCellsView;
    if (wv) { wv.update = () => null; const fall = wv.fall as THREE.Object3D | null | undefined; if (fall) fall.visible = false; }
    if (cv) { const cu = cv.update.bind(cv); cv.update = (s, _pi, ...rest) => cu(s, -1, ...rest); }
  }
  if (fs.marks !== undefined && game.fineGround?.syncMarks) {
    const fg = game.fineGround, sync = fg.syncMarks!.bind(fg);
    let cache: { key: string; list: PlanetState['craterlets'] } | null = null;
    fg.syncMarks = (planet: PlanetState, ...rest: unknown[]) => {
      if (!fs.marks) return sync({ ...planet, craterlets: [] }, ...rest);
      const focus = fg.focus as THREE.Vector3, key = `${Math.round(focus.x / (4 * P))},${Math.round(focus.y / (4 * P))},${Math.round(focus.z / (4 * P))}`;
      if (!cache || cache.key !== key) cache = { key, list: marksAround(planet, focus, P) };
      return sync({ ...planet, craterlets: cache.list }, ...rest);
    };
  }

  // the camera on the path (placed before the sky, the fine patch and every render read it)
  let s = 0;
  const eye = new THREE.Vector3(), at = new THREE.Vector3(), up = new THREE.Vector3();
  const place = () => {
    const planet = game.match!.state.planets[pi];
    if (!planet) return;
    pathEye(planet, centre, P, s, eye, at, up);
    if (game.actionHost) game.actionHost.pitch = GPU_AIM_LIFT;
    camera.position.copy(eye); camera.up.copy(up); camera.lookAt(at); camera.updateMatrixWorld();
  };
  for (const name of ['renderFrame', 'updateFineGround'] as const) {
    const fn = game[name]?.bind(game);
    if (fn) game[name] = () => { place(); fn(); };
  }

  // the measure: the frame in series, the CPU in the frame's callbacks, the timer over each render (a query a call), the rAF interval
  const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  let measuring = false, frame = 0, active = false;
  const pending: { q: WebGLQuery; frame: number; pass: string }[] = [], gpuByFrame = new Map<number, number>(), raf: number[] = [], cpuByFrame = new Map<number, number>(), serial = new Map<number, number>();
  const passByFrame = new Map<number, Record<string, number>>();
  let draws = 0, tris = 0, frames = 0, auroraFrames = 0, frameCalls = 0, frameTris = 0;
  // 384: which of the composer's passes is drawing (each pass's renderer.render calls are timed under its name; with fx=0
  // the view is drawn with no composer, as 'scene')
  let inPass: string | null = null;
  for (const cp of game.sceneObjs!.composer?.passes ?? []) {
    const own = cp as { render: (...a: unknown[]) => unknown }, run = own.render.bind(cp);
    own.render = (...a: unknown[]) => { inPass = passLabel(cp); try { return run(...a); } finally { inPass = null; } };
  }
  const render = renderer.render.bind(renderer);
  // (a pixel of the canvas read back waits out the frame's work: Chrome's gl.finish() returns without waiting, and a read of
  // another framebuffer waits only for the work on it)
  const px = new Uint8Array(4), waitGpu = () => {
    const fb = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
    if (fb) gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
    if (fb) gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  };
  renderer.render = (sc, cam) => {
    if (cam === camera) place();
    const q = measuring && ext && !active ? gl.createQuery() : null;
    if (q) { gl.beginQuery(ext!.TIME_ELAPSED_EXT, q); active = true; }
    render(sc, cam);
    if (q) { gl.endQuery(ext!.TIME_ELAPSED_EXT); active = false; pending.push({ q, frame, pass: inPass ?? (cam === camera ? 'scene' : 'other') }); }
    // 384: the game's render() keeps renderer.info across the composer's passes (autoReset off) and resets it once a frame,
    // so the frame's draws are its value after the frame's last call — summed per call they counted the view ~18 times
    if (measuring) { frameCalls = renderer.info.render.calls; frameTris = renderer.info.render.triangles; }
  };
  const poll = () => {
    if (!ext) return;
    const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT) as boolean;
    while (pending.length && gl.getQueryParameter(pending[0].q, gl.QUERY_RESULT_AVAILABLE)) {
      const { q, frame: f, pass } = pending.shift()!;
      const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
      gl.deleteQuery(q);
      if (disjoint) gpuByFrame.set(f, NaN); else gpuByFrame.set(f, (gpuByFrame.get(f) ?? 0) + ns / 1e6);
      if (!disjoint) { const e = passByFrame.get(f) ?? {}; e[pass] = (e[pass] ?? 0) + ns / 1e6; passByFrame.set(f, e); }
    }
  };
  let lastT = -1;
  const nativeRaf = window.requestAnimationFrame.bind(window);
  window.requestAnimationFrame = (cb: FrameRequestCallback) => nativeRaf(t => {
    if (t !== lastT) {
      if (measuring && lastT >= 0) { raf.push(t - lastT); frames++; draws += frameCalls; tris += frameTris; if (game.auroras?.ovals.some(o => o.mesh.visible && (o.material.uniforms.uGain?.value ?? 0) > 1e-3)) auroraFrames++; }
      frameCalls = frameTris = 0;
      lastT = t; frame++; poll();
    }
    const c0 = performance.now();
    try { cb(t); } finally {
      if (measuring) cpuByFrame.set(frame, (cpuByFrame.get(frame) ?? 0) + performance.now() - c0);
      if (measuring && run.sync !== false) { waitGpu(); serial.set(frame, (serial.get(frame) ?? 0) + performance.now() - c0); }
    }
  });

  banner(`${label} — warming up`);
  await sleep(run.warm * 1000);
  measuring = true;
  const t0 = performance.now(), T = run.sec * 1000;
  banner(`${label} — measuring`);
  while (performance.now() - t0 < T) { s = (performance.now() - t0) / T; await sleep(16); }
  measuring = false;
  s = 1;
  for (let k = 0; k < 30 && pending.length; k++) { await sleep(50); poll(); }
  const gpu = [...gpuByFrame.values()].filter(Number.isFinite);
  result.gpu = ext && gpu.length ? percentiles(gpu) : null;
  const done = [...gpuByFrame.entries()].filter(([, ms]) => Number.isFinite(ms)).map(([f]) => passByFrame.get(f) ?? {});
  const sum = done.reduce((n, e) => n + Object.values(e).reduce((a, b) => a + b, 0), 0);
  if (sum > 0) {
    const names = [...new Set(done.flatMap(e => Object.keys(e)))];
    result.passes = Object.fromEntries(names.map(n => [n, done.reduce((a, e) => a + (e[n] ?? 0), 0) / sum]));
  }
  result.frame = run.sync !== false ? percentiles([...serial.values()]) : null;
  result.raf = percentiles(raf);
  result.cpu = percentiles([...cpuByFrame.values()]);
  result.draws = frames ? Math.round(draws / frames) : 0;
  result.tris = frames ? Math.round(tris / frames) : 0;
  result.auroraShown = frames ? auroraFrames / frames : 0;
  result.weather = (game.weatherView?.kindNow as string | null | undefined) ?? null;
  finish();
}

/** The device line: the GPU's name where the browser gives it, the canvas, the pixel ratio, the timer. */
function deviceLine(): string {
  try {
    const c = document.createElement('canvas'), gl = c.getContext('webgl2');
    const dbg = gl?.getExtension('WEBGL_debug_renderer_info');
    const name = gl && dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : 'GPU unknown';
    const timer = gl?.getExtension('EXT_disjoint_timer_query_webgl2') ? 'GPU timer' : 'no GPU timer';
    return `${name}; ${innerWidth}×${innerHeight} @${+devicePixelRatio.toFixed(2)}x; ${timer}`;
  } catch { return 'device unknown'; }
}

// ---- the page's entry -----------------------------------------------------------------------------------------------------------

if (params?.has('gpucheck') && typeof document !== 'undefined') {
  const mode = params.get('gpucheck');
  const start = () => {
    if (mode === 'done') { const run = load(); if (run) showTable(run); return; }
    if (mode === 'run') {
      const run = load(), i = Number(params.get('gpustep'));
      if (run && run.steps[i] && run.results.length === i) void runStep(run, i);
      else banner('GPU check: no run in progress here — open ?gpucheck to start one');
      return;
    }
    // a new run: the plan from the query, then its first step
    const scenes = (params.get('gpuscenes') ?? 'mega,story').split(',').filter(s => GPU_SCENES[s]);
    const features = (params.get('gpufeatures') ?? Object.keys(GPU_FEATURES).filter(f => !GPU_CUTS.includes(f)).join(',')).split(',').filter(f => GPU_FEATURES[f]);
    const run: Run = {
      steps: gpuCheckPlan(scenes, features), at: Date.now(), results: [],
      sec: Number(params.get('gpusec') ?? 8), warm: Number(params.get('gpuwarm') ?? 3), device: deviceLine(), extra: params.get('gpuextra') ?? '', sync: params.get('gpusync') !== '0',
    };
    save(run);
    banner(`GPU check: ${run.steps.length} steps (~${Math.ceil(run.steps.length * (run.sec + run.warm + 15) / 60)} min) — keep this tab in front`);
    setTimeout(() => go(gpuStepQuery(run.steps[0], 0, run.extra)), 2500);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
}
